import { Controller } from "@hotwired/stimulus"
import { replaceByFetch } from "./mixins/replace_by_fetch"
import { formSerialization } from "./mixins/form_serialization"

/*
 * Search controller sends a query to the server and displays the results.
 *
 * It has to be explicitly shown via show action.
 *
 * Example:
 *
 *      %form{method: 'GET',
 *            action: url_for_searched_objects_retrieval,
 *            data: { controller: 'search',
 *                    search: { target: 'form',
 *                              wait: { value: 1200 },
 *                              hidden_class: 'hidden' }}
 *        %input{type: 'text',
 *               name: 'q',
 *               data: { action: 'input->search#search',
 *                       search_target: 'input' }}
 *        %div{ data: { search_target: 'spinner' }}
 *          %button{data: { action: 'click->search#submit' }}
 *        %div{data: { search_target: 'results hideable' }}
 *
 */
export default class extends Controller {
  static values = {
    wait: 750,
  }
  static targets = [
    "form",
    "input",
    "results",
    "spinner",
    "hideable",
    "resultsCount",
    "viewportBounded",
    "errorTemplate",
  ]
  static classes = ["hidden"]

  connect() {
    super.connect()
    formSerialization(this)
    replaceByFetch(this)
    this.hideableTarget.classList.add(this.hiddenClass)
    if (this.hasViewportBoundedTarget) {
      this._viewportResizeListener = this._fitViewportBoundedTarget.bind(this)
      window.addEventListener("resize", this._viewportResizeListener)
    }
  }

  disconnect() {
    this._removeAbandonListener()
    if (this._viewportResizeListener) {
      window.removeEventListener("resize", this._viewportResizeListener)
    }
    super.disconnect()
  }

  decrement() {
    if (this.hasResultsCountTarget) {
      let count = parseInt(this.resultsCountTarget.innerText)
      if (count > 0) {
        this.resultsCountTarget.innerText = count - 1
      }
    }
  }

  _addAbandonListener() {
    if (!this._clickOutsideListener) {
      this._clickOutsideListener = this._clickOutside.bind(this)
      window.addEventListener("click", this._clickOutsideListener)
    }
  }

  _removeAbandonListener() {
    if (this._clickOutsideListener) {
      window.removeEventListener("click", this._clickOutsideListener)
      this._clickOutsideListener = null
    }
  }

  _clickOutside(event) {
    if (this._clickedOutside(event)) {
      this.abandon()
    }
  }

  loseFocus(event) {
    if (event.key === "Escape") {
      this.abandon()
    }
    if (event.key === "Tab" && this.inputTarget.value.length === 0) {
      this.abandon()
    }
  }

  _clickedOutside(event) {
    const clickedWithin = this.element === event.target || this.element.contains(event.target)

    return !clickedWithin
  }

  abandon() {
    // this can happen when hideable is displayed after the same click event
    // by another controller
    if (!this.hasHideableTarget) {
      return
    }
    this.hideableTarget.classList.add(this.hiddenClass)
    if (this.hideableTarget.nodeName === "DIALOG") {
      this.hideableTarget.close()
    }

    this._removeAbandonListener()
    this.abandonedAt = new Date()
  }

  clear(event) {
    event.preventDefault()
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
  }

  // shorcut for clearing the input and abandoning
  // without stopping of the event propagation
  clearAndAbandon() {
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.abandon()
  }

  show() {
    if (new Date() - this.abandonedAt < 100) {
      // if the search was abandoned less than 100ms ago, do not show it again
      return
    }

    this.hideableTarget.classList.remove(this.hiddenClass)
    if (this.hideableTarget.nodeName === "DIALOG") {
      this.hideableTarget.show()
    }
    this._fitViewportBoundedTarget()
    this._addAbandonListener()
  }

  _fitViewportBoundedTarget() {
    if (!this.hasViewportBoundedTarget) return

    const availableWidth =
      document.documentElement.clientWidth - this.viewportBoundedTarget.getBoundingClientRect().left
    this.viewportBoundedTarget.style.maxWidth = `${Math.max(0, availableWidth)}px`
  }

  toggle({ focusFirstField = false }) {
    if (this.hideableTarget.classList.contains(this.hiddenClass)) {
      this.show()
      if (focusFirstField) {
        this.focusFirstField()
      }
    } else {
      this.abandon()
    }
  }

  focusFirstField() {
    this.hideableTarget.querySelector("input").focus()
  }

  search(event) {
    event.preventDefault()
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this._sendQuery(event.target.value)
    }, this.waitValue)
  }

  submit(event) {
    event.preventDefault()
    clearTimeout(this.timeout)
    this._sendQuery(this.inputTarget.value)
  }

  _sendQuery(query) {
    if (!query || query.length === 0) {
      this.resultsTarget.innerHTML = ""
      return
    }

    let action = this.formTarget.action || this.formTarget.dataset.turboFrameUrl
    let method = this.formTarget.method || this.formTarget.dataset.method
    this.replaceByFetch(action, method.toUpperCase(), this._fetchOptions())
    this.show()
  }

  _fetchOptions() {
    let options = {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")["content"],
        "X-Turbo-Frame": "no-layout",
        Accept: "text/html",
      },
      spinnerTarget: this.spinnerTarget,
      successHandler: (html) => {
        this.resultsTarget.innerHTML = html
      },
      onApplicationError: (message, response) => {
        this._showSearchError(message, response.applicationError.code === "no_active_customer")
      },
      onHttpError: (response) => {
        console.error(`Search request failed with status ${response.status}`)
        this._showSearchError(window.I18n.t("errors.messages.unexpected_error"))
      },
      onFetchError: (error) => {
        console.error("Search request failed", error)
        this._showSearchError(window.I18n.t("errors.messages.unexpected_error"))
      },
    }

    const formData = new FormData()
    this.collectInputValues(this.formTarget, formData)
    if (Array.from(formData.entries()).length === 0) {
      return options
    }

    options.data = formData

    return options
  }

  _showSearchError(message, showMessage = false) {
    if (!this.hasErrorTemplateTarget) {
      this.resultsTarget.textContent = message
      return
    }

    const errorContent = this.errorTemplateTarget.content.cloneNode(true)
    if (showMessage) {
      errorContent.querySelector("[data-search-error-message]").textContent = message
    }
    this.resultsTarget.replaceChildren(errorContent)
  }
}
